/**
 * PeerPrep Medication Admin Panel
 * 
 * Review AI-classified medications, approve/edit classifications,
 * and monitor the medication matching system.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';
import type { DrugCard, MedicationClass } from '../types/medication';

const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY
);

// ============================================
// Types
// ============================================

interface MedicationWithClass extends DrugCard {
  class_description?: string;
  rebuttal_strategies?: string[];
}

type TabType = 'needs_review' | 'ai_classified' | 'all' | 'stats';

// ============================================
// Stats Card Component
// ============================================

function StatsCard({ label, value, subtext, color = 'slate' }: {
  label: string;
  value: number | string;
  subtext?: string;
  color?: 'slate' | 'blue' | 'amber' | 'emerald' | 'rose';
}) {
  const colorClasses = {
    slate: 'bg-slate-50 border-slate-200',
    blue: 'bg-blue-50 border-blue-200',
    amber: 'bg-amber-50 border-amber-200',
    emerald: 'bg-emerald-50 border-emerald-200',
    rose: 'bg-rose-50 border-rose-200',
  };

  return (
    <div className={`p-4 rounded-lg border ${colorClasses[color]}`}>
      <p className="text-sm text-slate-600">{label}</p>
      <p className="text-2xl font-semibold mt-1">{value}</p>
      {subtext && <p className="text-xs text-slate-500 mt-1">{subtext}</p>}
    </div>
  );
}

// ============================================
// Medication Row Component
// ============================================

interface MedicationRowProps {
  medication: MedicationWithClass;
  classes: MedicationClass[];
  onApprove: (id: string) => void;
  onUpdateClass: (id: string, classId: string) => void;
  onDelete: (id: string) => void;
  expanded: boolean;
  onToggleExpand: () => void;
}

function MedicationRow({
  medication,
  classes,
  onApprove,
  onUpdateClass,
  onDelete,
  expanded,
  onToggleExpand,
}: MedicationRowProps) {
  const [selectedClassId, setSelectedClassId] = useState(medication.class_id);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleClassChange = async (newClassId: string) => {
    setSelectedClassId(newClassId);
    setIsUpdating(true);
    await onUpdateClass(medication.id, newClassId);
    setIsUpdating(false);
  };

  const getSourceBadge = () => {
    switch (medication.source) {
      case 'ai_classified':
        return <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">AI Classified</span>;
      case 'manual_override':
        return <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">Manual Override</span>;
      case 'local':
        return <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">Local/Seed</span>;
      default:
        return null;
    }
  };

  const getConfidenceColor = () => {
    if (medication.confidence >= 0.9) return 'text-emerald-600';
    if (medication.confidence >= 0.8) return 'text-blue-600';
    if (medication.confidence >= 0.6) return 'text-amber-600';
    return 'text-rose-600';
  };

  return (
    <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
      {/* Main Row */}
      <div
        className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50"
        onClick={onToggleExpand}
      >
        <div className="flex items-center gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h4 className="font-medium text-slate-800 capitalize">
                {medication.generic_name}
              </h4>
              {medication.needs_review && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                  Needs Review
                </span>
              )}
              {getSourceBadge()}
            </div>
            <p className="text-sm text-slate-500">
              {medication.brand_names.slice(0, 3).join(', ') || 'No brand names'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-sm text-slate-600">{medication.class_name}</p>
            <p className={`text-xs font-medium ${getConfidenceColor()}`}>
              {Math.round(medication.confidence * 100)}% confidence
            </p>
          </div>
          <span className="text-slate-400">{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="border-t border-slate-200 p-4 bg-slate-50 space-y-4">
          {/* Details Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-slate-500">DEA Schedule</p>
              <p className="font-medium">{medication.dea_schedule || 'Not controlled'}</p>
            </div>
            <div>
              <p className="text-slate-500">High PA Burden</p>
              <p className="font-medium">{medication.high_prior_auth_burden ? 'Yes' : 'No'}</p>
            </div>
            <div>
              <p className="text-slate-500">Created</p>
              <p className="font-medium">{new Date(medication.created_at).toLocaleDateString()}</p>
            </div>
            <div>
              <p className="text-slate-500">Source</p>
              <p className="font-medium capitalize">{medication.source.replace('_', ' ')}</p>
            </div>
          </div>

          {/* Indications */}
          {medication.indications.length > 0 && (
            <div>
              <p className="text-sm text-slate-500 mb-1">Indications</p>
              <div className="flex flex-wrap gap-1">
                {medication.indications.map((ind, i) => (
                  <span key={i} className="text-xs px-2 py-1 bg-white border border-slate-200 rounded">
                    {ind}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Synonyms */}
          {medication.synonyms.length > 0 && (
            <div>
              <p className="text-sm text-slate-500 mb-1">Synonyms (for matching)</p>
              <p className="text-sm text-slate-700">{medication.synonyms.join(', ')}</p>
            </div>
          )}

          {/* Common Denial Reasons */}
          {medication.common_denial_reasons && medication.common_denial_reasons.length > 0 && (
            <div>
              <p className="text-sm text-slate-500 mb-1">Common Denial Reasons</p>
              <ul className="text-sm text-slate-700 list-disc list-inside">
                {medication.common_denial_reasons.map((reason, i) => (
                  <li key={i}>{reason}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-3 pt-2 border-t border-slate-200">
            {/* Class Override */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-600">Class:</label>
              <select
                value={selectedClassId}
                onChange={(e) => handleClassChange(e.target.value)}
                disabled={isUpdating}
                className="text-sm px-2 py-1 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {classes.map((cls) => (
                  <option key={cls.class_id} value={cls.class_id}>
                    {cls.class_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex-1" />

            {/* Approve Button */}
            {medication.needs_review && (
              <button
                onClick={() => onApprove(medication.id)}
                className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded hover:bg-emerald-700 transition-colors"
              >
                Approve Classification
              </button>
            )}

            {/* Delete Button */}
            <button
              onClick={() => {
                if (confirm(`Delete "${medication.generic_name}" from the database?`)) {
                  onDelete(medication.id);
                }
              }}
              className="px-3 py-1.5 text-rose-600 text-sm border border-rose-300 rounded hover:bg-rose-50 transition-colors"
            >
              Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// Main Admin Panel Component
// ============================================

export function MedicationAdminPanel() {
  const [medications, setMedications] = useState<MedicationWithClass[]>([]);
  const [classes, setClasses] = useState<MedicationClass[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('needs_review');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Load data
  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [medsResponse, classesResponse] = await Promise.all([
        supabase.from('medications_with_class').select('*').order('created_at', { ascending: false }),
        supabase.from('medication_classes').select('*').order('class_name'),
      ]);

      if (medsResponse.error) throw medsResponse.error;
      if (classesResponse.error) throw classesResponse.error;

      setMedications(medsResponse.data || []);
      setClasses(classesResponse.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter medications based on tab and search
  const filteredMedications = medications.filter((med) => {
    // Tab filter
    if (activeTab === 'needs_review' && !med.needs_review) return false;
    if (activeTab === 'ai_classified' && med.source !== 'ai_classified') return false;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        med.generic_name.toLowerCase().includes(query) ||
        med.brand_names.some((b) => b.toLowerCase().includes(query)) ||
        med.class_name.toLowerCase().includes(query)
      );
    }

    return true;
  });

  // Stats
  const stats = {
    total: medications.length,
    aiClassified: medications.filter((m) => m.source === 'ai_classified').length,
    needsReview: medications.filter((m) => m.needs_review).length,
    avgConfidence: medications.length
      ? Math.round(
          (medications.reduce((sum, m) => sum + m.confidence, 0) / medications.length) * 100
        )
      : 0,
  };

  // Handlers
  const handleApprove = async (id: string) => {
    const { error } = await supabase
      .from('medications')
      .update({ needs_review: false })
      .eq('id', id);

    if (error) {
      console.error('Failed to approve:', error);
      return;
    }

    setMedications((prev) =>
      prev.map((m) => (m.id === id ? { ...m, needs_review: false } : m))
    );
  };

  const handleUpdateClass = async (id: string, classId: string) => {
    const selectedClass = classes.find((c) => c.class_id === classId);
    if (!selectedClass) return;

    const { error } = await supabase
      .from('medications')
      .update({
        class_id: classId,
        source: 'manual_override',
        confidence: 1.0,
        needs_review: false,
      })
      .eq('id', id);

    if (error) {
      console.error('Failed to update class:', error);
      return;
    }

    setMedications((prev) =>
      prev.map((m) =>
        m.id === id
          ? {
              ...m,
              class_id: classId,
              class_name: selectedClass.class_name,
              source: 'manual_override' as const,
              confidence: 1.0,
              needs_review: false,
            }
          : m
      )
    );
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from('medications').delete().eq('id', id);

    if (error) {
      console.error('Failed to delete:', error);
      return;
    }

    setMedications((prev) => prev.filter((m) => m.id !== id));
  };

  // Tab configuration
  const tabs: { key: TabType; label: string; count?: number }[] = [
    { key: 'needs_review', label: 'Needs Review', count: stats.needsReview },
    { key: 'ai_classified', label: 'AI Classified', count: stats.aiClassified },
    { key: 'all', label: 'All Medications', count: stats.total },
    { key: 'stats', label: 'Stats' },
  ];

  if (isLoading) {
    return (
      <div className="p-8 text-center">
        <div className="w-8 h-8 border-2 border-slate-300 border-t-blue-500 rounded-full animate-spin mx-auto" />
        <p className="text-slate-500 mt-4">Loading medications...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-rose-600">{error}</p>
        <button
          onClick={loadData}
          className="mt-4 px-4 py-2 bg-slate-100 rounded hover:bg-slate-200"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">Medication Admin</h1>
          <p className="text-slate-500">Review AI classifications and manage the medication database</p>
        </div>
        <button
          onClick={loadData}
          className="px-4 py-2 text-sm bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
        >
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span
                className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.key ? 'bg-blue-100' : 'bg-slate-100'
                }`}
              >
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Stats Tab */}
      {activeTab === 'stats' && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatsCard label="Total Medications" value={stats.total} color="slate" />
          <StatsCard
            label="AI Classified"
            value={stats.aiClassified}
            subtext={`${Math.round((stats.aiClassified / stats.total) * 100) || 0}% of total`}
            color="blue"
          />
          <StatsCard
            label="Needs Review"
            value={stats.needsReview}
            subtext={stats.needsReview > 0 ? 'Requires attention' : 'All clear!'}
            color={stats.needsReview > 0 ? 'amber' : 'emerald'}
          />
          <StatsCard
            label="Avg Confidence"
            value={`${stats.avgConfidence}%`}
            subtext="Across all medications"
            color={stats.avgConfidence >= 80 ? 'emerald' : 'amber'}
          />
        </div>
      )}

      {/* List Tabs */}
      {activeTab !== 'stats' && (
        <>
          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search by name, brand, or class..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 pl-10 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">🔍</span>
          </div>

          {/* List */}
          {filteredMedications.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              {searchQuery ? 'No medications match your search' : 'No medications in this category'}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredMedications.map((med) => (
                <MedicationRow
                  key={med.id}
                  medication={med}
                  classes={classes}
                  onApprove={handleApprove}
                  onUpdateClass={handleUpdateClass}
                  onDelete={handleDelete}
                  expanded={expandedId === med.id}
                  onToggleExpand={() => setExpandedId(expandedId === med.id ? null : med.id)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default MedicationAdminPanel;